import { BASE_LOCALE } from "@outschool/localization";
import { dayjs } from "@outschool/time";
import _ from "lodash";

interface TimetableTimes {
  f4_8: boolean;
  f12_4: boolean;
  f8_12: boolean;
}

export interface Timetable {
  mon: TimetableTimes;
  tue: TimetableTimes;
  wed: TimetableTimes;
  thu: TimetableTimes;
  fri: TimetableTimes;
  sat: TimetableTimes;
  sun: TimetableTimes;
}

export type HourlyTimetable = Record<keyof Timetable, number[]>;

interface CompressedTimetable {
  mon: { [key: string]: boolean };
  tue: { [key: string]: boolean };
  wed: { [key: string]: boolean };
  thu: { [key: string]: boolean };
  fri: { [key: string]: boolean };
  sat: { [key: string]: boolean };
  sun: { [key: string]: boolean };
}

export const DEFAULT_TIMETABLE: Timetable = Object.freeze({
  mon: { f8_12: true, f12_4: true, f4_8: true },
  tue: { f8_12: true, f12_4: true, f4_8: true },
  wed: { f8_12: true, f12_4: true, f4_8: true },
  thu: { f8_12: true, f12_4: true, f4_8: true },
  fri: { f8_12: true, f12_4: true, f4_8: true },
  sat: { f8_12: true, f12_4: true, f4_8: true },
  sun: { f8_12: true, f12_4: true, f4_8: true },
});

export const DAYS_OF_WEEK_SHORT_NAME: Record<keyof Timetable, string> = {
  mon: "Mon",
  tue: "Tue",
  wed: "Wed",
  thu: "Thu",
  fri: "Fri",
  sat: "Sat",
  sun: "Sun",
};

export const ISO_DAYS_OF_WEEK = [
  "Mon",
  "Tue",
  "Wed",
  "Thu",
  "Fri",
  "Sat",
  "Sun",
] as const;

export const ISO_DAYS_OF_WEEK_LOWER_CASE = [
  "mon",
  "tue",
  "wed",
  "thu",
  "fri",
  "sat",
  "sun",
] as const;
export const TIME_OF_DAY_KEYS: string[] = ["f8_12", "f12_4", "f4_8"];
export const TIME_OF_DAY_LABELS: string[] = [
  "8am - 12pm",
  "12 - 4pm",
  "4 - 8pm",
];

export const WORK_HOURS_OF_DAY: number[] = [
  0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20, 21,
  22, 23,
];

export const EMPTY_HOURLY_TIMETABLE: HourlyTimetable = Object.freeze({
  mon: [],
  tue: [],
  wed: [],
  thu: [],
  fri: [],
  sat: [],
  sun: [],
});

export const DEFAULT_HOURLY_TIMETABLE: HourlyTimetable = Object.freeze({
  mon: WORK_HOURS_OF_DAY,
  tue: WORK_HOURS_OF_DAY,
  wed: WORK_HOURS_OF_DAY,
  thu: WORK_HOURS_OF_DAY,
  fri: WORK_HOURS_OF_DAY,
  sat: WORK_HOURS_OF_DAY,
  sun: WORK_HOURS_OF_DAY,
});

const READABLE_TIMES: Map<string, string> = new Map()
  .set("f8_12", "8am - 12pm")
  .set("f12_4", "12pm - 4pm")
  .set("f4_8", "4pm - 8pm");

const READABLE_DAYS: Map<string, string> = new Map()
  .set("mon", "Monday")
  .set("tue", "Tuesday")
  .set("wed", "Wednesday")
  .set("thu", "Thursday")
  .set("fri", "Friday")
  .set("sat", "Saturday")
  .set("sun", "Sunday");

const TIME_OF_DAY_KEYS_TO_HOURS: {
  f8_12: number[];
  f12_4: number[];
  f4_8: number[];
} = {
  f8_12: [8, 9, 10, 11],
  f12_4: [12, 13, 14, 15],
  f4_8: [16, 17, 18, 19],
};

/**
 * Creates timetables from class requests by converting the class request
 * timetable "range keys" to "hours" in the teacher's timezone.
 * E.g: A timetable of a classRequest from New York for {mon: {f8_12: true}, ...}
 becomes {mon: {5: true, 6: true, 7: true, 8: true}, ...} in Los Angeles
 */
export function convertClassRequestsToTimetables(
  classRequests: {
    timetable: Timetable;
    timeZone: string;
  }[],
  userTimeZone: string,
): HourlyTimetable[] {
  return classRequests.map(cr => {
    return expandTimetable(cr.timetable, cr.timeZone, userTimeZone);
  });
}

/**
 * Turns a timetable with ranges per day like {mon: {f8_12: true, ...}, ...}
 * into a timetable with hours per day like {mon: [8, 9, 10, ...], tue: ...}.
 * It shifts the time zone when timeZone and targetTimeZone are different.
 * @param timetable
 * @param timeZone
 * @param targetTimeZone
 */
export function expandTimetable(
  timetable: Timetable,
  timeZone: string,
  targetTimeZone: string,
): HourlyTimetable {
  const expandedTimetable: HourlyTimetable = {
    mon: [],
    tue: [],
    wed: [],
    thu: [],
    fri: [],
    sat: [],
    sun: [],
  };
  for (const day of ISO_DAYS_OF_WEEK_LOWER_CASE) {
    for (const key of TIME_OF_DAY_KEYS) {
      if (
        Boolean(timetable[day as keyof Timetable][key as keyof TimetableTimes])
      ) {
        for (const hour of TIME_OF_DAY_KEYS_TO_HOURS[
          key as keyof TimetableTimes
        ]) {
          const adjustedTime = dayjs
            .tz(timeZone)
            .isoWeekday(ISO_DAYS_OF_WEEK_LOWER_CASE.indexOf(day) + 1)
            .hour(hour)
            .tz(targetTimeZone);
          const adjustedDay = ISO_DAYS_OF_WEEK_LOWER_CASE[
            adjustedTime.isoWeekday() - 1
          ] as keyof Timetable;
          const adjustedHour = adjustedTime.hour();
          expandedTimetable[adjustedDay].push(adjustedHour);
        }
      }
    }
  }
  return expandedTimetable;
}

export const HOURS_OF_DAY: number[] = [
  0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20, 21,
  22, 23,
];

/**
 * Merges hour lines that have the same pattern with the previous pattern
 * E.g:
 *    M T W T F S S
 *  8 x x x x x
 *  9 x x x x x
 * 10 x x x x x
 * 11 x x x x x
 * 12 x x x x x
 * 13
 * 14 x x x x x
 * 15 x x x x x
 * --after compression--
 *         M T W T F S S
 * 8am-1pm x x x x x
 * 2pm-4pm x x x x x
 */
export function compressTimetable(
  timeZoneAdjustedTimetable: HourlyTimetable,
  userTimeZone: string,
): {
  compressedTimetable: CompressedTimetable;
  timeLabels: string[];
} {
  const compressedTimetable: any = {};
  for (const day of ISO_DAYS_OF_WEEK_LOWER_CASE) {
    compressedTimetable[day] = {};
  }
  const timeLabels: string[] = [];
  let startHour: null | number = null;
  for (const [hour, i] of HOURS_OF_DAY.entries()) {
    if (!startHour) {
      startHour = hour;
      continue;
    }
    const startPattern = ISO_DAYS_OF_WEEK_LOWER_CASE.map(day =>
      timeZoneAdjustedTimetable[day as keyof Timetable].includes(
        <number>startHour,
      ),
    );
    const currentPattern = ISO_DAYS_OF_WEEK_LOWER_CASE.map(day =>
      timeZoneAdjustedTimetable[day as keyof Timetable].includes(hour),
    );
    const patternChanged = !_.isEqual(startPattern, currentPattern);
    const isTheEnd = i === HOURS_OF_DAY.length - 1;
    if (patternChanged || isTheEnd) {
      let endHour = hour;
      if (!patternChanged && isTheEnd) {
        endHour = hour + 1;
      }
      addTimeLabel(
        compressedTimetable as CompressedTimetable,
        timeZoneAdjustedTimetable,
        timeLabels,
        userTimeZone,
        startHour,
        endHour,
      );
      if (patternChanged && isTheEnd) {
        addTimeLabel(
          compressedTimetable as CompressedTimetable,
          timeZoneAdjustedTimetable,
          timeLabels,
          userTimeZone,
          hour,
          hour + 1,
        );
      }
      startHour = hour;
    }
  }

  return {
    compressedTimetable: compressedTimetable as CompressedTimetable,
    timeLabels: timeLabels.filter(l =>
      ISO_DAYS_OF_WEEK_LOWER_CASE.some(day => compressedTimetable[day][l]),
    ),
  };
}

function addTimeLabel(
  compressedTimetable: CompressedTimetable,
  timeZoneAdjustedTimetable: HourlyTimetable,
  timeLabels: string[],
  userTimeZone: string,
  startHour: number,
  endHour: number,
): void {
  const startDayjs = dayjs.tz(userTimeZone).hour(startHour);
  const endDayjs = dayjs.tz(userTimeZone).hour(endHour);
  let timeLabel: any = null;
  if (startDayjs.format("a") === endDayjs.format("a")) {
    timeLabel = `${startDayjs.format("h")} - ${endDayjs.format("ha")}`;
  } else {
    timeLabel = `${startDayjs.format("ha")} - ${endDayjs.format("ha")}`;
  }
  timeLabels.push(timeLabel);
  ISO_DAYS_OF_WEEK_LOWER_CASE.forEach(
    day =>
      (compressedTimetable[day as keyof Timetable][timeLabel] =
        timeZoneAdjustedTimetable[day as keyof Timetable].includes(startHour)),
  );
}

export function isMatchForDayTimeSlot(
  timetable: HourlyTimetable,
  startDayjs: dayjs.Dayjs,
  endDayjs: dayjs.Dayjs,
): boolean {
  // Keep start day in base locale so that Timetable will match
  const day = startDayjs
    .locale(BASE_LOCALE)
    .format("ddd")
    .toLowerCase() as keyof Timetable;
  const startHour = startDayjs.hour();
  const endHour =
    endDayjs.minute() === 0 ? endDayjs.hour() : endDayjs.hour() + 1;
  for (let hour = startHour; hour < endHour; hour++) {
    if (!timetable[day].includes(hour)) {
      return false;
    }
  }
  return true;
}

export function numHoursAvailable(hourlyTimetable: HourlyTimetable): number {
  return _.sumBy(_.values(hourlyTimetable), hours => hours.length);
}

/**
 *
 * @param timetable
 * takes a timetable and returns an array of days that includes
 * the full day string and an array of times that were selected
 * @returns { label: string, times: string[] }[]
 */
export function getOrderedTimetable(timetable: Timetable) {
  if (!timetable) {
    return [];
  }

  const orderedDays = [];

  for (const day of ISO_DAYS_OF_WEEK_LOWER_CASE) {
    const selectedTimes = [];

    for (const time of TIME_OF_DAY_KEYS) {
      // @ts-ignore
      if (timetable[day][time]) {
        selectedTimes.push(READABLE_TIMES.get(time));
      }
    }

    orderedDays.push({ label: READABLE_DAYS.get(day), times: selectedTimes });
  }

  return orderedDays;
}

export function timetableHasSelection(timetable: Timetable): Boolean {
  if (!timetable) {
    return false;
  }

  const hasSelection = ISO_DAYS_OF_WEEK_LOWER_CASE.some(
    day =>
      // @ts-ignore
      timetable[day][TIME_OF_DAY_KEYS[0]] ||
      // @ts-ignore
      timetable[day][TIME_OF_DAY_KEYS[1]] ||
      // @ts-ignore
      timetable[day][TIME_OF_DAY_KEYS[2]],
  );

  return hasSelection;
}
